/**
 * Q&A の管理(admin)。一覧・追加・編集・削除と、CSV の書き出し/一括取込(2026-09-17 発注者指示)。
 * CSV は書き出したものを Excel で直してそのまま取り込める(ID 空欄=追加、ID あり=変更)。
 * CSV に無い既存の項目は「削除する」にチェックしたときだけ消す。
 */
import { useMemo, useState } from 'react';
import { useCurrentUser } from '@core/auth/AuthProvider';
import Modal from '@core/components/Modal';
import { SearchBox, matchKeyword } from '@core/components/tableTools';
import { useToast } from '@core/components/Toast';
import { fmtTs } from '@core/lib/auditLog';
import { readCsvFile } from '@core/lib/csv';
import { todayStr } from '@core/lib/date';
import { downloadCsv } from '@core/masters/csvImport';
import QaEditModal from './QaEditModal';
import { planQaImport, qaToCsv, sortQa, type QaImportPlan } from './qaCsv';
import { applyQaImport, type QaItem } from './qaRepo';

export default function QaManageTab({ items, onChanged }: { items: QaItem[]; onChanged: () => void }) {
  const [editing, setEditing] = useState<QaItem | 'new' | null>(null);
  const [importing, setImporting] = useState(false);
  const [keyword, setKeyword] = useState('');
  const sorted = useMemo(() => sortQa(items).filter((q) => matchKeyword(keyword, q.category, q.question, q.answer)), [items, keyword]);
  const categories = useMemo(() => [...new Set(sortQa(items).map((q) => q.category))], [items]);
  const nextOrder = items.reduce((m, q) => Math.max(m, q.sort_order), 0) + 10;

  return (
    <div className="card">
      <h3 style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        Q&Aの管理
        <span className="hint">{items.length}件</span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn-line" onClick={() => setEditing('new')}>
            ＋追加
          </button>
          <button className="btn-line" onClick={() => downloadCsv(`qa_${todayStr()}.csv`, qaToCsv(items))}>
            CSVを書き出す
          </button>
          <button className="btn-line" onClick={() => setImporting(true)}>
            CSVで一括更新
          </button>
        </span>
      </h3>
      <div className="toolbar" style={{ marginBottom: 12 }}>
        <div className="tool-filter">
          <SearchBox value={keyword} onChange={setKeyword} placeholder="カテゴリ・質問・回答で検索" style={{ minWidth: 220 }} />
        </div>
      </div>
      {sorted.length === 0 ? (
        <div className="empty">{items.length === 0 ? 'Q&Aはまだありません(「＋追加」か「CSVで一括更新」から登録してください)' : '該当するQ&Aがありません'}</div>
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th className="num">表示順</th>
                <th>カテゴリ</th>
                <th>質問</th>
                <th>回答</th>
                <th>更新日</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((q) => (
                <tr key={q.id}>
                  <td data-label="表示順" className="num">
                    {q.sort_order}
                  </td>
                  <td data-label="カテゴリ">
                    <span className="badge b-plan">{q.category}</span>
                  </td>
                  <td data-label="質問" style={{ fontWeight: 600, minWidth: 200 }}>
                    {q.question}
                  </td>
                  <td data-label="回答" className="hint" style={{ minWidth: 240 }}>
                    {q.answer.length > 60 ? `${q.answer.slice(0, 60)}…` : q.answer}
                  </td>
                  <td data-label="更新日" className="hint">
                    {fmtTs(q.updated_at, false)}
                  </td>
                  <td>
                    <button className="txn-link" onClick={() => setEditing(q)}>
                      編集
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <QaEditModal
          item={editing === 'new' ? null : editing}
          categories={categories}
          nextOrder={nextOrder}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            onChanged();
          }}
        />
      )}
      {importing && (
        <QaImportModal
          items={items}
          onClose={() => setImporting(false)}
          onDone={() => {
            setImporting(false);
            onChanged();
          }}
        />
      )}
    </div>
  );
}

function QaImportModal({ items, onClose, onDone }: { items: QaItem[]; onClose: () => void; onDone: () => void }) {
  const user = useCurrentUser();
  const toast = useToast();
  const [fileName, setFileName] = useState('');
  const [plan, setPlan] = useState<QaImportPlan | null>(null);
  const [deleteMissing, setDeleteMissing] = useState(false);
  const [busy, setBusy] = useState(false);

  async function onFile(file: File | undefined) {
    if (!file) return;
    try {
      setFileName(file.name);
      setPlan(planQaImport(await readCsvFile(file), items));
      setDeleteMissing(false);
    } catch {
      toast('CSVを読めませんでした');
    }
  }

  const deletes = plan && deleteMissing ? plan.missing : [];
  const total = plan ? plan.creates.length + plan.updates.length + deletes.length : 0;

  async function run() {
    if (!plan) return;
    setBusy(true);
    try {
      const r = await applyQaImport({ creates: plan.creates, updates: plan.updates, deletes }, user.id);
      toast(`取り込みました(追加 ${r.created}件・変更 ${r.updated}件・削除 ${r.deleted}件)`);
      onDone();
    } catch (e) {
      toast(e instanceof Error ? e.message : '取り込みに失敗しました');
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} width="min(760px, 94vw)">
      <h3>CSVで一括更新</h3>
      <p className="hint" style={{ margin: '6px 0 12px' }}>
        列は ID・カテゴリ・質問・回答・表示順。「CSVを書き出す」で出したファイルを直して取り込むのが確実です(ID が空欄の行は追加、ID がある行は変更)。
      </p>
      <input type="file" accept=".csv,text/csv" onChange={(e) => void onFile(e.target.files?.[0])} disabled={busy} />
      {plan && (
        <div style={{ marginTop: 14, fontSize: 13 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <b>{fileName}</b>
            <span className="badge b-ok">追加 {plan.creates.length}</span>
            <span className="badge b-plan">変更 {plan.updates.length}</span>
            <span className="badge">変更なし {plan.unchanged}</span>
            {plan.errors.length > 0 && <span className="badge b-ng">エラー {plan.errors.length}</span>}
          </div>
          {plan.errors.length > 0 && (
            <ul style={{ paddingLeft: 20, marginTop: 8, color: 'var(--red)' }}>
              {plan.errors.slice(0, 20).map((e) => (
                <li key={e.row}>
                  {e.row}行目: {e.message}
                </li>
              ))}
              {plan.errors.length > 20 && <li>ほか {plan.errors.length - 20}件</li>}
            </ul>
          )}
          {plan.updates.length > 0 && (
            <details style={{ marginTop: 8 }}>
              <summary>変更する項目({plan.updates.length}件)</summary>
              <ul style={{ paddingLeft: 20 }}>
                {plan.updates.map((u) => (
                  <li key={u.item.id}>{u.next.question}</li>
                ))}
              </ul>
            </details>
          )}
          {plan.missing.length > 0 && plan.errors.length === 0 && (
            <div className="gray-box" style={{ marginTop: 10 }}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="checkbox" checked={deleteMissing} onChange={(e) => setDeleteMissing(e.target.checked)} />
                CSVに無い既存の {plan.missing.length}件を削除する
              </label>
              <ul className="hint" style={{ paddingLeft: 20, margin: '6px 0 0' }}>
                {plan.missing.slice(0, 10).map((q) => (
                  <li key={q.id}>
                    【{q.category}】{q.question}
                  </li>
                ))}
                {plan.missing.length > 10 && <li>ほか {plan.missing.length - 10}件</li>}
              </ul>
            </div>
          )}
        </div>
      )}
      <div className="modal-actions">
        <button className="btn-green" onClick={() => void run()} disabled={busy || !plan || plan.errors.length > 0 || total === 0}>
          {busy ? '取り込み中…' : `取り込む(${total}件)`}
        </button>
        <button className="btn-line" onClick={onClose} disabled={busy}>
          閉じる
        </button>
      </div>
    </Modal>
  );
}
